import React, { useMemo, useState } from "react";
import {
  AccountData,
  MassText,
  TrackingData,
  TrackingOverview,
  TrackingType,
} from "@markit/common.types";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import { Box, CircularProgress } from "@mui/material";
import { AntTab, AntTabs } from "../../../AntTabs";
import FlatList from "flatlist-react/lib";
import { GetLongDate } from "../../../../utils/GetLongDate";
import { GetTime } from "@markit/common.utils";
import { API } from "../../../../API";
import ProfilePic from "../../../ProfilePic";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";

type MassTextLinkAnalyticsItemProps = {
  item: AccountData;
  trackingOverview: TrackingOverview;
  massText: MassText;
  attachmentId: string;
  isSelected: boolean;
  onPress: () => void;
  showConversions?: boolean;
};

export const MassTextLinkAnalyticsItem = ({
  item,
  trackingOverview,
  isSelected,
  massText,
  attachmentId,
  onPress,
  showConversions,
}: MassTextLinkAnalyticsItemProps) => {
  const [tabValue, setTabValue] = useState(0);
  const [trackingData, setTrackingData] = useState<TrackingData[]>([]);
  const [loading, setLoading] = useState(true);

  const linkListToShow = useMemo(
    () =>
      tabValue === 0
        ? trackingData.filter((tracker) => tracker.type === TrackingType.OPEN)
        : trackingData.filter(
            (tracker) => tracker.type === TrackingType.CONVERSION
          ),
    [tabValue, trackingData]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (isSelected) {
      const { trackingData } = await API.tracking.getUserTrackingData({
        userId: massText.sentBy,
        recipientId: item.uid,
        massTextId: massText.id,
        campaignId: massText.campaignId,
        attachmentId: attachmentId,
      });
      setTrackingData(trackingData);
      setLoading(false);
    }
  }, [
    attachmentId,
    isSelected,
    item.uid,
    massText.campaignId,
    massText.id,
    massText.sentBy,
  ]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <div key={item.uid} className="ColumnNormal">
      <div
        onClick={onPress}
        className="AlignedRowSpaced"
        style={{
          cursor: "pointer",
          pointerEvents: trackingOverview.numOpens === 0 ? "none" : "all",
        }}
      >
        <div className="GuestInfo" style={{ cursor: "pointer" }}>
          <div
            className="GuestInfo"
            style={{
              cursor: "pointer",
              textDecoration: "none",
              color: Colors.BLACK,
            }}
          >
            <ProfilePic user={item} size={48} forcePointer />
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {item.fullName}
            </span>
          </div>
        </div>
        <div className="AlignedRowSelect" style={{ gap: 10 }}>
          <div className="AlignedRow" style={{ gap: 2 }}>
            <Icon icon="mdi:cursor-default-click" height={12} />
            <span style={{ fontSize: 12 }}>{trackingOverview.numOpens}</span>
          </div>
          {showConversions ? (
            <div className="AlignedRow" style={{ gap: 2 }}>
              <Icon icon="ion:pricetag" height={12} style={{ marginTop: 1 }} />
              <span style={{ fontSize: 12 }}>
                {trackingOverview.numConversions}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {isSelected ? (
        <div
          className="MassTextsSectionContainer"
          style={{ marginTop: 14, cursor: "auto", paddingTop: 0 }}
        >
          <Box>
            <AntTabs value={tabValue} onChange={handleChange}>
              <AntTab
                label={`Clicks (${trackingOverview.numOpens})`}
                sx={{ padding: 0, fontSize: 13 }}
              />
              {showConversions ? (
                <AntTab
                  label={`Conversions (${trackingOverview.numConversions})`}
                  isDisabled={trackingOverview.numConversions === 0}
                  sx={{ fontSize: 13 }}
                />
              ) : null}
            </AntTabs>
          </Box>
          <FlatList
            list={linkListToShow}
            renderWhenEmpty={() =>
              loading ? (
                <div className="Centering" style={{ paddingBlock: 14 }}>
                  <CircularProgress style={{ color: Colors.GRAY1 }} size={18} />
                </div>
              ) : (
                <span className="bodyMedium">
                  Unable to display at this time...
                </span>
              )
            }
            renderItem={(item) => (
              <div className="AlignedRow" style={{ marginTop: 14 }}>
                <Icon
                  icon={
                    tabValue === 0 ? "mdi:cursor-default-click" : "ion:pricetag"
                  }
                  height={12}
                  style={{ marginRight: 5 }}
                />
                <span style={{ marginRight: 15, fontSize: 14 }}>
                  {tabValue === 0 ? "Click" : "Conversion"}
                </span>
                <span style={{ fontSize: 14 }}>
                  {GetLongDate(item.createdAt, true, true, false)},{" "}
                  {GetTime(item.createdAt)}
                </span>
              </div>
            )}
          />
        </div>
      ) : null}
      <hr />
    </div>
  );
};
